import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

function Landing() {
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const featureInterval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 4);
    }, 2000);

    return () => {
      clearInterval(featureInterval);
    };
  }, []);

  const creatures = [
    { 
      name: 'Sprint Sprout', 
      color: 'bg-green-100', 
      description: '90-day focused growth',
      icon: '🌳' // Tree for long-term growth
    },
    { 
      name: 'Weekly Willow', 
      color: 'bg-blue-100', 
      description: '7-day planning cycles',
      icon: '🌿' // Leaf for weekly planning
    },
    { 
      name: 'Daily Daisy', 
      color: 'bg-yellow-100', 
      description: 'Daily goal tracking',
      icon: '🌼' // Flower for daily blooming
    },
    { 
      name: 'Habit Hedgehog', 
      color: 'bg-purple-100', 
      description: 'Consistent progress',
      icon: '🦔' // Hedgehog for steady habits
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <img 
                src="https://gckvitd1qe.ufs.sh/f/IjvP5MdEoCL2wk9k9OYEOIcNHVeSo6TlRb7BLM1sqDdrm0PU" 
                alt="PlantAhead Logo" 
                className="h-10 w-10 transform transition-transform duration-300 hover:scale-110"
              />
              <span className="ml-2 text-xl font-semibold text-gray-900">PlantAhead</span>
            </div>
            <Link
              to="/auth"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-gray-700 hover:text-primary-600 transition-all duration-300 hover:scale-105"
            >
              Login
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-transparent"></div>
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-transparent sm:pb-16 md:pb-20 lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 lg:mt-16 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl tracking-tight font-bold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Life moves too fast for</span>
                  <span className="block text-primary-600">yearly goals</span>
                </h1>
                <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                  In today's fast-paced world, waiting a year to achieve your goals isn't an option. Break them down into focused 90-day sprints and start making progress today.
                </p>
                <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                  <Link
                    to="/auth?register=true"
                    className="block px-8 py-3 text-base font-medium rounded-lg text-white bg-primary-600 md:py-4 md:text-lg md:px-10 transition-all duration-300 hover:bg-primary-700 hover:scale-[1.02]"
                  >
                    Start Your First Sprint
                  </Link>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Feature Section with Creatures */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Why 90 Days?</h2>
            <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
              The perfect timeframe for real progress
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Long enough to achieve meaningful results, short enough to maintain focus and momentum.
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-8">
              {creatures.map((creature, index) => (
                <div
                  key={creature.name}
                  className={`relative p-6 rounded-xl transition-all duration-500 transform ${
                    activeFeature === index ? 'scale-105' : 'scale-100'
                  } ${creature.color} hover:shadow-lg`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className={`w-12 h-12 rounded-full ${
                        activeFeature === index ? 'animate-bounce' : ''
                      } bg-white shadow-md flex items-center justify-center`}>
                        <span className="text-2xl">{creature.icon}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-lg leading-6 font-medium text-gray-900">{creature.name}</div>
                      <div className="mt-2 text-base text-gray-500">{creature.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Life in Weeks Demo Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Time is Precious</h2>
            <p className="mt-2 text-3xl leading-8 font-bold tracking-tight text-gray-900 sm:text-4xl">
              Make every week count
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our unique "Life in Weeks" visualization helps you understand the value of time and make the most of every 90-day sprint.
            </p>
          </div>

          {/* Demo Grid */}
          <div className="max-w-lg mx-auto mt-8">
            <div className="grid grid-cols-52 gap-[1px] bg-gray-200 p-1 rounded-lg">
              {Array.from({ length: 52 * 5 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-full pt-[100%] rounded-sm ${
                    i < 90 ? 'bg-primary-500' : 'bg-gray-100'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-4">
              A glimpse of your next 5 years, week by week
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base text-gray-400">
              © 2025 PlantAhead. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;